'use client';

import * as React from 'react';
import Link from 'next/link';
import { 
  MessageSquare, 
  Mail, 
  Phone, 
  Send,
  Loader2,
  ArrowLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { SiteFooter } from '@/components/public/site-footer';
import { api, API_ENDPOINTS } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import type { CompanyContactSettings } from '@/types/admin';
import { useContentText, useTranslations } from '@/lib/i18n';

const DEFAULT_COMPANY_CONTACT: CompanyContactSettings = {
  address: 'Herengracht 123, 1015 BG Amsterdam, Netherlands',
  mapEmbedHtml: '',
  email: 'support@pinkdude.cloud',
  phone: '+31 20 123 4567',
  whatsapp: '',
  viber: '',
  skype: '',
  linkedin: '',
  officeName: 'PinkDude HQ',
  officeDescription: '',
  socials: [],
  logoUrl: '',
  companyId: '',
  vatNumber: '',
};

export default function ContactPage() {
  const t = useTranslations('contact.contact');
  const contentContact = useContentText('contact');
  const { user, isAuthenticated } = useAuth();
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [isSubmitted, setIsSubmitted] = React.useState(false);
  const [companyContact, setCompanyContact] = React.useState<CompanyContactSettings>(DEFAULT_COMPANY_CONTACT);
  const [formData, setFormData] = React.useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
  });

  React.useEffect(() => {
    let isMounted = true;

    const fetchCompanyContact = async () => {
      try {
        const data = await api.get<CompanyContactSettings>(API_ENDPOINTS.SETTINGS_COMPANY_CONTACT_PUBLIC);
        if (!isMounted) return;
        setCompanyContact({
          ...DEFAULT_COMPANY_CONTACT,
          ...data,
          socials: data.socials ?? [],
        });
      } catch {
        if (isMounted) {
          setCompanyContact(DEFAULT_COMPANY_CONTACT);
        }
      }
    };

    fetchCompanyContact();

    return () => {
      isMounted = false;
    };
  }, []);

  const getAutofillDefaults = React.useCallback(() => {
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim();

    return {
      name: fullName || user?.username || '',
      email: user?.pinkdudeEmail || user?.email || '',
      phone: user?.phoneNumber || user?.phone || '',
    };
  }, [
    user?.email,
    user?.firstName,
    user?.lastName,
    user?.phone,
    user?.phoneNumber,
    user?.pinkdudeEmail,
    user?.username,
  ]);

  React.useEffect(() => {
    if (!isAuthenticated || !user) return;

    const defaults = getAutofillDefaults();
    setFormData((prev) => {
      const next = {
        ...prev,
        name: prev.name || defaults.name,
        email: prev.email || defaults.email,
        phone: prev.phone || defaults.phone,
      };

      if (
        next.name === prev.name &&
        next.email === prev.email &&
        next.phone === prev.phone
      ) {
        return prev;
      }

      return next;
    });
  }, [getAutofillDefaults, isAuthenticated, user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));
    
    setIsSubmitting(false);
    setIsSubmitted(true);
  };

  const emailValue = companyContact.email?.trim() || '';
  const phoneValue = companyContact.phone?.trim() || '';
  const telHref = phoneValue ? `tel:${phoneValue.replace(/[^\d+]/g, '')}` : '';

  if (isSubmitted) {
    return (
      <div className="min-h-dvh flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-6">
            <div className="w-16 h-16 rounded-full bg-success/15 dark:bg-success/20 flex items-center justify-center mx-auto mb-4">
              <MessageSquare className="h-8 w-8 text-success-text" />
            </div>
            <h2 className="text-2xl font-semibold mb-2">{t('message-sent')}</h2>
            <p className="text-muted-foreground mb-6">
              {t('thank-you-for-contacting-us-we-ll-get-ba')}</p>
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button onClick={() => {
                setIsSubmitted(false);
                const defaults = isAuthenticated
                  ? getAutofillDefaults()
                  : { name: '', email: '', phone: '' };
                setFormData({ ...defaults, subject: '', message: '' });
              }}>
                {t('send-another-message')}</Button>
              {isAuthenticated && (
                <Button variant="outline" asChild>
                  <Link href="/dashboard">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    {t('back-to-dashboard')}</Link>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-dvh flex flex-col">
      <div className="flex-1 py-12 px-4">
        <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          {isAuthenticated && (
            <div className="mb-4 flex justify-center">
              <Button variant="outline" asChild>
                <Link href="/dashboard">
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  {t('back-to-dashboard')}</Link>
              </Button>
            </div>
          )}
          <h1 className="text-4xl font-bold mb-4">{t('contact-us')}</h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            {t('have-a-question-or-need-help-we-re-here')}</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Contact Info */}
          <div className="space-y-6">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-primary/10">
                    <Mail className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{t('email')}</h3>
                    {emailValue ? (
                      <a
                        href={`mailto:${emailValue}`}
                        className="text-sm font-medium text-primary-text underline underline-offset-2 decoration-primary/60 transition-colors hover:text-primary-text/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 rounded-sm"
                      >
                        {emailValue}
                      </a>
                    ) : (
                      <p className="text-sm text-muted-foreground">{t('n-a')}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="pt-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-lg bg-primary/10">
                    <Phone className="h-6 w-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="font-semibold">{t('phone')}</h3>
                    {phoneValue ? (
                      <a
                        href={telHref}
                        className="text-sm font-medium text-primary-text underline underline-offset-2 decoration-primary/60 transition-colors hover:text-primary-text/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 rounded-sm"
                      >
                        {phoneValue}
                      </a>
                    ) : (
                      <p className="text-sm text-muted-foreground">{t('n-a')}</p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {companyContact.socials.length > 0 && (
              <Card>
                <CardContent className="pt-6">
                  <h3 className="font-semibold mb-3">{t('social-links')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {companyContact.socials.map((social, index) => (
                      <a
                        key={`${social.label}-${index}`}
                        href={social.url}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center px-3 py-1.5 rounded-full border border-border text-sm hover:bg-accent transition-colors"
                      >
                        {contentContact(social.label)}
                      </a>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>{t('send-a-message')}</CardTitle>
                <CardDescription>
                  {t('fill-out-the-form-below-and-we-ll-get-ba')}</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">{t('name')}</Label>
                      <Input
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder={t('your-name')}
                        required
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">{t('email')}</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder={t('your-email-com')}
                        required
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">{t('phone')}</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+1 555 123 4567"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="subject">{t('subject')}</Label>
                    <Input
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      placeholder={t('how-can-we-help')}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">{t('message')}</Label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder={t('tell-us-more-about-your-inquiry')}
                      rows={6}
                      required
                      className="w-full px-3 py-2 rounded-lg border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                    />
                  </div>

                  <Button type="submit" className="w-full" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t('sending')}</>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        {t('send-message')}</>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
        </div>
      </div>
      <SiteFooter scope="publicFooter" />
    </div>
  );
}
